/**
 * services/sync/OperationApplicator.ts
 * In-place transformation functions for applying sync operations to records
 *
 * These helpers are designed to run within the import transaction context
 * where op logging is already suppressed via markAsImportContext().
 *
 * Design principles:
 * - In-place mutation: modifies and returns the same record reference
 * - No table writes: caller handles batching and persistence
 * - No notifications: caller handles after all ops processed
 * - Uses operation metadata: timestamp and deviceId from the operation itself
 *
 * IMPORTANT: These helpers set updatedAt from operation.timestamp on every call.
 * Phase 2 (processing loop) MUST apply ops in LWW order per record to ensure
 * updatedAt reflects the final winning operation's timestamp.
 *
 * Phase 2 (processing loop) is responsible for:
 * - Initializing records for new-record ops
 * - LWW comparison to determine winning ops
 * - Applying ops in correct order so updatedAt doesn't regress
 * - Clearing tombstone fields for resurrection
 * - Section timestamp updates
 */

import type {
  SyncOperation,
  SetOperation,
  ClearOperation,
  DeleteOperation,
} from '@/types/sync/SyncOperation';
import { isTombstoned } from '@/utils/tombstone';

/**
 * Record type used during operation application.
 * Dynamically typed at sync boundary to handle any table's schema.
 */
export type SyncRecord = Record<string, unknown>;

/**
 * Path segments that must be blocked to prevent prototype pollution attacks.
 * These are special JavaScript object properties that could be exploited
 * if allowed in dot-notation paths from imported operations.
 */
const DANGEROUS_PATH_SEGMENTS = ['__proto__', 'constructor', 'prototype'] as const;

/**
 * Check if a value is a plain object that can be traversed.
 * Returns false for null, arrays, primitives, and class instances.
 */
function isTraversableObject(value: unknown): value is Record<string, unknown> {
  if (value === null || value === undefined) return false;
  if (Array.isArray(value)) return false;
  if (typeof value !== 'object') return false;
  // Check for plain object (not a class instance like Date, Map, etc.)
  const proto = Object.getPrototypeOf(value);
  return proto === Object.prototype || proto === null;
}

/**
 * Set a value at a dot-notation path, creating intermediate objects as needed.
 *
 * Handles nested paths like 'trading.strategy.description' by traversing
 * the object structure and creating missing intermediate objects.
 *
 * Security: Blocks dangerous path segments (__proto__, constructor, prototype)
 * to prevent prototype pollution attacks from malformed operations.
 *
 * @param record - Record to modify (will be mutated)
 * @param path - Dot-notation path (e.g., 'trading.strategy.name')
 * @param value - Value to set at the path
 * @returns true if a change was made, false if operation was skipped
 *
 * @example
 * const record = {};
 * setNestedValue(record, 'trading.strategy.name', 'Scalping');
 * // record = { trading: { strategy: { name: 'Scalping' } } }
 */
export function setNestedValue(
  record: SyncRecord,
  path: string,
  value: unknown
): boolean {
  const segments = path.split('.');

  // Check for dangerous segments (prototype pollution guard)
  for (const segment of segments) {
    if (DANGEROUS_PATH_SEGMENTS.includes(segment as typeof DANGEROUS_PATH_SEGMENTS[number])) {
      console.warn(
        `[OperationApplicator] Blocked dangerous path segment "${segment}" in path "${path}". ` +
        `Operation skipped to prevent prototype pollution.`
      );
      return false;
    }
  }

  // Single segment (no dots) - direct assignment
  if (segments.length === 1) {
    record[path] = value;
    return true;
  }

  // Traverse/create nested structure
  // Use slice to get all but last segment for traversal
  const pathSegments = segments.slice(0, -1);
  const leafSegment = segments[segments.length - 1];

  // Safety check (shouldn't happen given segments.length > 1 check above)
  if (leafSegment === undefined) {
    return false;
  }

  let current: Record<string, unknown> = record;
  for (const segment of pathSegments) {
    // If intermediate doesn't exist or isn't traversable, create/overwrite with {}
    if (!isTraversableObject(current[segment])) {
      current[segment] = {};
    }

    current = current[segment] as Record<string, unknown>;
  }

  // Set the leaf value
  current[leafSegment] = value;
  return true;
}

/**
 * Clear (set to null) a value at a dot-notation path.
 *
 * Handles nested paths like 'trading.strategy.description' by traversing
 * the object structure. If any intermediate path is missing or not an object,
 * this is a no-op (nothing to clear).
 *
 * Security: Blocks dangerous path segments (__proto__, constructor, prototype)
 * to prevent prototype pollution attacks from malformed operations.
 *
 * @param record - Record to modify (will be mutated)
 * @param path - Dot-notation path (e.g., 'trading.strategy.name')
 * @returns true if a change was made, false if operation was skipped (no-op)
 *
 * @example
 * const record = { trading: { strategy: { name: 'Scalping' } } };
 * clearNestedValue(record, 'trading.strategy.name');
 * // record = { trading: { strategy: { name: null } } }
 */
export function clearNestedValue(record: SyncRecord, path: string): boolean {
  const segments = path.split('.');

  // Check for dangerous segments (prototype pollution guard)
  for (const segment of segments) {
    if (DANGEROUS_PATH_SEGMENTS.includes(segment as typeof DANGEROUS_PATH_SEGMENTS[number])) {
      console.warn(
        `[OperationApplicator] Blocked dangerous path segment "${segment}" in path "${path}". ` +
        `Operation skipped to prevent prototype pollution.`
      );
      return false;
    }
  }

  // Single segment (no dots) - direct assignment
  if (segments.length === 1) {
    record[path] = null;
    return true;
  }

  // Traverse to the parent of the leaf
  // Use slice to get all but last segment for traversal
  const pathSegments = segments.slice(0, -1);
  const leafSegment = segments[segments.length - 1];

  // Safety check (shouldn't happen given segments.length > 1 check above)
  if (leafSegment === undefined) {
    return false;
  }

  let current: Record<string, unknown> = record;
  for (const segment of pathSegments) {
    // If intermediate doesn't exist or isn't traversable, nothing to clear
    if (!isTraversableObject(current[segment])) {
      return false; // No-op
    }

    current = current[segment] as Record<string, unknown>;
  }

  // Set the leaf to null
  current[leafSegment] = null;
  return true;
}

/**
 * Apply a set operation to a record.
 *
 * Sets the specified field to the provided value and updates
 * the record's updatedAt timestamp from the operation.
 *
 * @param record - Current record state (will be mutated)
 * @param operation - The set operation to apply
 * @returns The modified record (same reference as input)
 *
 * @example
 * const record = { id: '123', name: 'Old Name' };
 * applySetOperation(record, {
 *   op: 'set',
 *   field: 'name',
 *   value: 'New Name',
 *   timestamp: '2025-01-01T00:00:00Z',
 *   // ... other op fields
 * });
 * // record.name === 'New Name'
 * // record.updatedAt === '2025-01-01T00:00:00Z'
 */
export function applySetOperation(
  record: SyncRecord,
  operation: SetOperation
): SyncRecord {
  const changed = setNestedValue(record, operation.field, operation.value);
  if (changed) {
    record['updatedAt'] = operation.timestamp;
  }
  return record;
}

/**
 * Apply a clear operation to a record.
 *
 * Sets the specified field to null and updates the record's
 * updatedAt timestamp from the operation.
 *
 * @param record - Current record state (will be mutated)
 * @param operation - The clear operation to apply
 * @returns The modified record (same reference as input)
 *
 * @example
 * const record = { id: '123', notes: 'Some notes' };
 * applyClearOperation(record, {
 *   op: 'clear',
 *   field: 'notes',
 *   timestamp: '2025-01-01T00:00:00Z',
 *   // ... other op fields
 * });
 * // record.notes === null
 * // record.updatedAt === '2025-01-01T00:00:00Z'
 */
export function applyClearOperation(
  record: SyncRecord,
  operation: ClearOperation
): SyncRecord {
  const changed = clearNestedValue(record, operation.field);
  if (changed) {
    record['updatedAt'] = operation.timestamp;
  }
  return record;
}

/**
 * Apply a delete operation to a record.
 *
 * Sets tombstone fields (deletedAt, deletedByDevice) using the
 * operation's timestamp and deviceId, and updates updatedAt.
 *
 * Note: This creates a soft-delete tombstone, not a hard delete.
 * The record remains in the database with deletion metadata.
 *
 * @param record - Current record state (will be mutated)
 * @param operation - The delete operation to apply
 * @returns The tombstoned record (same reference as input)
 *
 * @example
 * const record = { id: '123', name: 'Trade' };
 * applyDeleteOperation(record, {
 *   op: 'delete',
 *   deviceId: 'device-abc',
 *   timestamp: '2025-01-01T00:00:00Z',
 *   // ... other op fields
 * });
 * // record.deletedAt === '2025-01-01T00:00:00Z'
 * // record.deletedByDevice === 'device-abc'
 * // record.updatedAt === '2025-01-01T00:00:00Z'
 */
export function applyDeleteOperation(
  record: SyncRecord,
  operation: DeleteOperation
): SyncRecord {
  record['deletedAt'] = operation.timestamp;
  record['deletedByDevice'] = operation.deviceId;
  record['updatedAt'] = operation.timestamp;
  return record;
}

/**
 * Apply any sync operation to a record.
 *
 * Dispatcher that routes to the appropriate handler based on
 * the operation's `op` type discriminant.
 *
 * @param record - Current record state (will be mutated)
 * @param operation - The sync operation to apply
 * @returns The modified record (same reference as input)
 * @throws Error if operation type is unknown (should never happen with typed ops)
 *
 * @example
 * const record = { id: '123' };
 * applyOperation(record, someSetOperation);
 * applyOperation(record, someClearOperation);
 * applyOperation(record, someDeleteOperation);
 */
export function applyOperation(
  record: SyncRecord,
  operation: SyncOperation
): SyncRecord {
  switch (operation.op) {
    case 'set':
      return applySetOperation(record, operation);
    case 'clear':
      return applyClearOperation(record, operation);
    case 'delete':
      return applyDeleteOperation(record, operation);
    default: {
      // Exhaustiveness check - TypeScript will error if a case is missing
      const exhaustiveCheck: never = operation;
      throw new Error(
        `[OperationApplicator] Unknown operation type: ${(exhaustiveCheck as SyncOperation).op}`
      );
    }
  }
}

// ============================================================================
// Phase 2: Operation Processing Loop
// ============================================================================

/**
 * Special field key used to track delete operations in the LWW winner map.
 * Delete operations target the entire record, not a specific field.
 */
const DELETE_FIELD_KEY = '__delete__';

/**
 * Create a consistent key for record lookups across LWW map and record grouping.
 *
 * @param table - Table name
 * @param recordId - Record ID
 * @returns Composite key in format "table:recordId"
 */
export function makeRecordKey(table: string, recordId: string): string {
  return `${table}:${recordId}`;
}

/**
 * Precedence tuple for LWW comparison.
 * Timestamp is primary key, seq is secondary, deviceId is tertiary tiebreaker.
 */
interface OpPrecedence {
  timestamp: string;
  seq: number;
  deviceId: string;
}

/**
 * Compare two operations for LWW precedence.
 *
 * Timestamp is primary key (later wins). If timestamps equal, seq is secondary.
 * If both equal, lexicographically higher deviceId wins as final tiebreaker.
 *
 * Uses locale-independent string comparison (< / >) instead of localeCompare
 * to ensure consistent ordering across all devices regardless of locale settings.
 * Timestamps are UTC ISO 8601 strings which sort correctly with lexicographic comparison.
 * DeviceIds are UUIDs (lowercase hex), so simple comparison is reliable.
 *
 * @param a - First operation's precedence
 * @param b - Second operation's precedence
 * @returns Positive if `a` wins, negative if `b` wins, 0 if equal
 *
 * @example
 * // Later timestamp wins
 * compareOpPrecedence(
 *   { timestamp: '2025-01-02T00:00:00Z', seq: 1, deviceId: 'A' },
 *   { timestamp: '2025-01-01T00:00:00Z', seq: 100, deviceId: 'B' }
 * ); // > 0 (a wins - later timestamp beats higher seq)
 *
 * @example
 * // Same timestamp - higher seq wins
 * compareOpPrecedence(
 *   { timestamp: '2025-01-01T00:00:00Z', seq: 5, deviceId: 'A' },
 *   { timestamp: '2025-01-01T00:00:00Z', seq: 3, deviceId: 'B' }
 * ); // > 0 (a wins)
 */
export function compareOpPrecedence(a: OpPrecedence, b: OpPrecedence): number {
  // Primary: timestamp (later wins)
  if (a.timestamp !== b.timestamp) {
    if (a.timestamp < b.timestamp) return -1;
    if (a.timestamp > b.timestamp) return 1;
  }
  // Secondary: seq (higher wins)
  if (a.seq !== b.seq) {
    return a.seq - b.seq;
  }
  // Tertiary: deviceId as tiebreaker (locale-independent comparison)
  if (a.deviceId < b.deviceId) return -1;
  if (a.deviceId > b.deviceId) return 1;
  return 0;
}

/**
 * Tracks the winning operation for a specific field (or __delete__ for record deletion).
 */
interface LWWWinner {
  timestamp: string;
  seq: number;
  deviceId: string;
  opIndex: number;
}

/**
 * Result of processing operations.
 */
export interface ProcessOperationsResult {
  /** Records modified by operations, keyed by "table:recordId" */
  modifiedRecords: Map<string, SyncRecord>;
  /** Table name for each modified record, keyed by "table:recordId" */
  recordTables: Map<string, string>;
  /** Updated seq tracking after processing */
  updatedLastAppliedSeq: Record<string, number>;
  /** Statistics for logging/debugging */
  stats: {
    totalOps: number;
    skippedMalformed: number;
    skippedIdempotent: number;
    skippedInvalidTimestamp: number;
    skippedStaleTimestamp: number;
    skippedFutureTimestamp: number;
    skippedLWW: number;
    appliedOps: number;
    newRecords: number;
    resurrections: number;
    clampedTimestamps: number;
  };
}

/**
 * Async function to fetch a record from the database.
 * Returns undefined if record doesn't exist.
 */
export type RecordFetcher = (
  table: string,
  recordId: string
) => Promise<SyncRecord | undefined>;

/**
 * Configuration for processOperations timestamp validation.
 */
export interface ProcessOperationsConfig {
  /** ISO timestamp - ops older than this are rejected as stale (must be canonical ISO if provided) */
  baselineFloorTimestamp?: string;
  /** Current time in ms for future check (default: Date.now()) - injectable for tests */
  currentTimeMs?: number;
}

/**
 * Validate that an operation has all required fields.
 * batchId is optional and should NOT be required.
 *
 * @param op - Operation to validate
 * @returns true if valid, false if malformed
 */
export function isValidOperation(op: unknown): op is SyncOperation {
  if (typeof op !== 'object' || op === null) {
    return false;
  }

  const o = op as Record<string, unknown>;

  // Check required base fields
  if (typeof o['table'] !== 'string' || o['table'].length === 0) return false;
  if (typeof o['recordId'] !== 'string' || o['recordId'].length === 0) return false;
  if (typeof o['seq'] !== 'number' || !Number.isInteger(o['seq'])) return false;
  if (typeof o['deviceId'] !== 'string' || o['deviceId'].length === 0) return false;
  if (typeof o['timestamp'] !== 'string' || o['timestamp'].length === 0) return false;
  if (typeof o['opId'] !== 'string' || o['opId'].length === 0) return false;

  // Check op type and type-specific fields
  const opType = o['op'];
  if (opType === 'set') {
    if (typeof o['field'] !== 'string' || o['field'].length === 0) return false;
    // value can be anything, including null/undefined
    return true;
  }
  if (opType === 'clear') {
    if (typeof o['field'] !== 'string' || o['field'].length === 0) return false;
    return true;
  }
  if (opType === 'delete') {
    return true;
  }

  return false; // Unknown op type
}

/**
 * Get the field key for an operation (used in LWW winner map).
 * Delete operations use a special key since they target the entire record.
 */
function getFieldKey(op: SyncOperation): string {
  if (op.op === 'delete') {
    return DELETE_FIELD_KEY;
  }
  return op.field;
}

/**
 * Check if a timestamp is in canonical UTC ISO 8601 format.
 * Canonical format: "YYYY-MM-DDTHH:mm:ss.sssZ" (e.g., "2025-01-01T12:00:00.000Z")
 *
 * This ensures timestamps can be safely compared lexicographically for LWW ordering.
 *
 * @param timestamp - Timestamp string to validate
 * @returns true if canonical ISO format, false otherwise
 */
export function isCanonicalIso(timestamp: string): boolean {
  const ms = Date.parse(timestamp);
  return !Number.isNaN(ms) && new Date(ms).toISOString() === timestamp;
}

/**
 * Result of timestamp validation.
 */
type TimestampValidationResult =
  | { valid: true }
  | { valid: false; reason: 'malformed' | 'stale' | 'future' };

/**
 * Validate a timestamp for LWW processing.
 *
 * Checks in order:
 * 1. Canonical ISO format (required for lexicographic LWW comparison)
 * 2. Not older than baseline floor (if floor provided)
 * 3. Not more than 1 hour in the future
 *
 * @param timestamp - Operation timestamp to validate
 * @param floorTimestamp - Baseline floor (guaranteed canonical if provided)
 * @param futureThresholdIso - Future threshold (canonical ISO)
 * @returns Validation result with reason if invalid
 */
function validateTimestamp(
  timestamp: string,
  floorTimestamp: string | undefined,
  futureThresholdIso: string
): TimestampValidationResult {
  // Check canonical ISO format
  if (!isCanonicalIso(timestamp)) {
    return { valid: false, reason: 'malformed' };
  }

  // Check stale (older than baseline floor) - string comparison works for canonical ISO
  if (floorTimestamp && timestamp < floorTimestamp) {
    return { valid: false, reason: 'stale' };
  }

  // Check future (more than 1 hour ahead) - string comparison works for canonical ISO
  if (timestamp > futureThresholdIso) {
    return { valid: false, reason: 'future' };
  }

  return { valid: true };
}

/**
 * Process a batch of sync operations with idempotency and LWW conflict resolution.
 *
 * This function:
 * 1. Filters out malformed operations
 * 2. Applies idempotency gate (skips already-applied ops)
 * 3. Validates timestamps (malformed, stale, future)
 * 4. Resolves conflicts using LWW (Last-Write-Wins by timestamp, then seq, then deviceId)
 * 5. Handles delete vs field ops (delete-wins-skip-fields rule)
 * 6. Handles resurrection (field ops winning over delete)
 * 7. Returns modified records for caller to persist
 *
 * @param operations - Operations to process
 * @param lastAppliedSeqByDevice - Current seq tracking (will not be mutated)
 * @param recordFetcher - Async function to fetch records from DB
 * @param config - Optional configuration for timestamp validation
 * @returns Processing result with modified records and stats
 */
export async function processOperations(
  operations: SyncOperation[],
  lastAppliedSeqByDevice: Record<string, number>,
  recordFetcher: RecordFetcher,
  config?: ProcessOperationsConfig
): Promise<ProcessOperationsResult> {
  const stats = {
    totalOps: operations.length,
    skippedMalformed: 0,
    skippedIdempotent: 0,
    skippedInvalidTimestamp: 0,
    skippedStaleTimestamp: 0,
    skippedFutureTimestamp: 0,
    skippedLWW: 0,
    appliedOps: 0,
    newRecords: 0,
    resurrections: 0,
    clampedTimestamps: 0,
  };

  // Compute thresholds once to prevent drift during long imports
  const nowMs = config?.currentTimeMs ?? Date.now();
  const futureThresholdIso = new Date(nowMs + 3600000).toISOString();
  const floorTimestamp = config?.baselineFloorTimestamp; // Pre-validated by caller

  // Track max seq per device for updating lastAppliedSeqByDevice
  const maxSeqByDevice: Record<string, number> = { ...lastAppliedSeqByDevice };

  // LWW winner map: "table:recordId:field" -> winner info
  const lwwWinners = new Map<string, LWWWinner>();

  // Track which ops pass validation and idempotency
  const validOps: Array<{ op: SyncOperation; index: number; effectiveTimestamp: string }> = [];

  // Track last-seen timestamp per device for monotonic clamping
  const lastSeenTimestampByDevice = new Map<string, string>();

  // Phase 1: Filter and track valid ops
  // ASSUMPTION: Operations are seq-ordered per device (guaranteed by getPendingOps index,
  // compactor sort, and delta filename order). If multi-device deltas ever appear, add pre-sort here.
  if (!import.meta.env.PROD) {
    // Dev-mode validation: verify per-device seq ordering before clamping depends on it
    const lastSeqByDevice = new Map<string, number>();
    for (const op of operations) {
      if (!op?.deviceId || typeof op.seq !== 'number') continue;
      const lastSeq = lastSeqByDevice.get(op.deviceId);
      if (lastSeq !== undefined && op.seq < lastSeq) {
        console.error('[OperationApplicator] DEV: seq ordering violation - clamping may be incorrect', {
          deviceId: op.deviceId,
          expectedSeqGreaterThan: lastSeq,
          actualSeq: op.seq,
        });
      }
      lastSeqByDevice.set(op.deviceId, op.seq);
    }
  }

  for (let i = 0; i < operations.length; i++) {
    const op = operations[i];

    // Validate operation structure
    if (!isValidOperation(op)) {
      console.warn('[OperationApplicator] Skipping malformed operation at index', i, op);
      stats.skippedMalformed++;
      continue;
    }

    // Idempotency gate: skip if seq <= lastApplied for this device
    // Note: Idempotent ops bypass timestamp validation - already validated on first application
    const lastApplied = lastAppliedSeqByDevice[op.deviceId] ?? 0;
    if (op.seq <= lastApplied) {
      stats.skippedIdempotent++;
      continue;
    }

    // Timestamp validation (malformed, stale, future)
    const tsValidation = validateTimestamp(op.timestamp, floorTimestamp, futureThresholdIso);
    if (!tsValidation.valid) {
      const { reason } = tsValidation;
      if (reason === 'malformed') {
        stats.skippedInvalidTimestamp++;
      } else if (reason === 'stale') {
        stats.skippedStaleTimestamp++;
      } else {
        stats.skippedFutureTimestamp++;
      }
      console.warn(`[OperationApplicator] Rejected timestamp: ${reason}`, {
        deviceId: op.deviceId,
        seq: op.seq,
        timestamp: op.timestamp,
        ...(reason === 'stale' && { floor: floorTimestamp }),
        ...(reason === 'future' && { threshold: futureThresholdIso }),
      });
      continue;
    }

    // Monotonic clamping: ensure per-device timestamp doesn't regress
    const lastSeenTs = lastSeenTimestampByDevice.get(op.deviceId);
    let effectiveTimestamp = op.timestamp;

    if (lastSeenTs && op.timestamp < lastSeenTs) {
      // Clamp forward to maintain monotonicity
      effectiveTimestamp = lastSeenTs;
      stats.clampedTimestamps++;
      console.warn('[OperationApplicator] Clamped regressing timestamp', {
        deviceId: op.deviceId,
        seq: op.seq,
        originalTimestamp: op.timestamp,
        clampedTo: effectiveTimestamp,
      });
    }

    // Update last-seen for this device (use effective, not original)
    lastSeenTimestampByDevice.set(op.deviceId, effectiveTimestamp);

    // Update max seq tracking (only after all validation passes)
    maxSeqByDevice[op.deviceId] = Math.max(maxSeqByDevice[op.deviceId] ?? 0, op.seq);

    validOps.push({ op, index: i, effectiveTimestamp });
  }

  // Build lookup map for effective timestamps (needed for Phase 3/4 which access ops by index)
  const effectiveTimestampByIndex = new Map<number, string>();
  for (const { index, effectiveTimestamp } of validOps) {
    effectiveTimestampByIndex.set(index, effectiveTimestamp);
  }

  // Phase 2: Build LWW winner map
  for (const { op, index, effectiveTimestamp } of validOps) {
    const recordKey = makeRecordKey(op.table, op.recordId);
    const fieldKey = getFieldKey(op);
    const lwwKey = `${recordKey}:${fieldKey}`;

    const currentWinner = lwwWinners.get(lwwKey);
    const opPrecedence: OpPrecedence = { timestamp: effectiveTimestamp, seq: op.seq, deviceId: op.deviceId };

    if (!currentWinner) {
      // First op for this field - it wins by default
      lwwWinners.set(lwwKey, { timestamp: effectiveTimestamp, seq: op.seq, deviceId: op.deviceId, opIndex: index });
    } else {
      // Compare with current winner
      const comparison = compareOpPrecedence(
        opPrecedence,
        { timestamp: currentWinner.timestamp, seq: currentWinner.seq, deviceId: currentWinner.deviceId }
      );
      if (comparison > 0) {
        // New op wins
        lwwWinners.set(lwwKey, { timestamp: effectiveTimestamp, seq: op.seq, deviceId: op.deviceId, opIndex: index });
      }
      // If comparison <= 0, current winner stays (new op loses LWW)
    }
  }

  // Phase 3: Group winning ops by record and determine delete-vs-field outcome
  // Map: recordKey -> { deleteWinner?, fieldWinners: Map<field, opIndex>, hasResurrection }
  interface RecordOutcome {
    table: string;
    recordId: string;
    deleteWinner: LWWWinner | null;
    fieldWinners: Map<string, number>; // field -> opIndex
    hasResurrection: boolean;
  }
  const recordOutcomes = new Map<string, RecordOutcome>();

  // Initialize outcomes for all records that have winning ops
  for (const [lwwKey, winner] of lwwWinners) {
    // Parse the lwwKey to extract table:recordId:field
    const lastColonIndex = lwwKey.lastIndexOf(':');
    const recordKey = lwwKey.substring(0, lastColonIndex);
    const fieldKey = lwwKey.substring(lastColonIndex + 1);

    let outcome = recordOutcomes.get(recordKey);
    if (!outcome) {
      // Extract table and recordId from recordKey
      const colonIndex = recordKey.indexOf(':');
      const table = recordKey.substring(0, colonIndex);
      const recordId = recordKey.substring(colonIndex + 1);
      outcome = {
        table,
        recordId,
        deleteWinner: null,
        fieldWinners: new Map(),
        hasResurrection: false,
      };
      recordOutcomes.set(recordKey, outcome);
    }

    if (fieldKey === DELETE_FIELD_KEY) {
      outcome.deleteWinner = winner;
    } else {
      outcome.fieldWinners.set(fieldKey, winner.opIndex);
    }
  }

  // Determine resurrection and delete-wins-skip-fields for each record
  for (const outcome of recordOutcomes.values()) {
    if (outcome.deleteWinner && outcome.fieldWinners.size > 0) {
      // Check if any field op wins over delete
      const deletePrecedence: OpPrecedence = {
        timestamp: outcome.deleteWinner.timestamp,
        seq: outcome.deleteWinner.seq,
        deviceId: outcome.deleteWinner.deviceId,
      };

      for (const opIndex of outcome.fieldWinners.values()) {
        const fieldOp = operations[opIndex];
        if (!fieldOp) continue; // Safety check
        const fieldPrecedence: OpPrecedence = {
          timestamp: effectiveTimestampByIndex.get(opIndex) ?? fieldOp.timestamp,
          seq: fieldOp.seq,
          deviceId: fieldOp.deviceId,
        };
        if (compareOpPrecedence(fieldPrecedence, deletePrecedence) > 0) {
          // At least one field op wins over delete -> resurrection
          outcome.hasResurrection = true;
          break;
        }
      }

      // If delete wins over ALL field ops, clear the fieldWinners (skip them)
      if (!outcome.hasResurrection) {
        // Delete wins - we'll only apply the delete, skip all field ops
        stats.skippedLWW += outcome.fieldWinners.size;
        outcome.fieldWinners.clear();
      }
    }
  }

  // Phase 4: Apply operations and build modified records
  const modifiedRecords = new Map<string, SyncRecord>();
  const recordTables = new Map<string, string>();

  for (const [recordKey, outcome] of recordOutcomes) {
    const { table, recordId, deleteWinner, fieldWinners, hasResurrection } = outcome;

    // Fetch existing record or initialize new one
    let record = await recordFetcher(table, recordId);
    const isNewRecord = record === undefined;

    // Track if existing record is tombstoned (for cross-delta resurrection detection)
    // Uses isTombstoned helper to align with tombstone semantics (non-empty string)
    const wasTombstoned = !isNewRecord && isTombstoned(record);

    if (isNewRecord) {
      // Initialize new record
      if (deleteWinner && !hasResurrection) {
        // Delete on missing record - create minimal tombstone
        record = { id: recordId };
      } else {
        record = { id: recordId };
      }
      stats.newRecords++;
    } else {
      // Clone to avoid mutating the fetched record directly
      record = { ...record };
    }

    // Collect ops to apply (in order for consistent updatedAt)
    // Include opIndex for effective timestamp lookup
    const opsToApply: Array<{ op: SyncOperation; opIndex: number }> = [];

    if (hasResurrection || !deleteWinner) {
      // Apply field ops - collect and sort by precedence
      for (const opIndex of fieldWinners.values()) {
        const op = operations[opIndex];
        if (op) opsToApply.push({ op, opIndex });
      }
    }

    if (deleteWinner && !hasResurrection) {
      // Delete wins - apply only delete
      const deleteOp = operations[deleteWinner.opIndex];
      if (deleteOp) opsToApply.push({ op: deleteOp, opIndex: deleteWinner.opIndex });
    }

    // Sort ops by precedence (lowest first, so highest ends up setting updatedAt)
    // Use effective timestamps for consistent ordering with clamped values
    opsToApply.sort((a, b) => compareOpPrecedence(
      {
        timestamp: effectiveTimestampByIndex.get(a.opIndex) ?? a.op.timestamp,
        seq: a.op.seq,
        deviceId: a.op.deviceId,
      },
      {
        timestamp: effectiveTimestampByIndex.get(b.opIndex) ?? b.op.timestamp,
        seq: b.op.seq,
        deviceId: b.op.deviceId,
      }
    ));

    // Set createdAt for new records created via ops (not delete-on-missing)
    // Use first op's effective timestamp as record creation time
    const firstEntry = opsToApply[0];
    if (isNewRecord && firstEntry && firstEntry.op.op !== 'delete') {
      record['createdAt'] = effectiveTimestampByIndex.get(firstEntry.opIndex) ?? firstEntry.op.timestamp;
    }

    // Apply ops in order, tracking if any field ops actually mutated the record
    // (blocked ops like dangerous path segments are no-ops and shouldn't trigger resurrection)
    // Wrap operations with effective timestamp for record metadata consistency
    let anyFieldOpMutated = false;
    for (const { op, opIndex } of opsToApply) {
      const effectiveTs = effectiveTimestampByIndex.get(opIndex) ?? op.timestamp;
      if (op.op !== 'delete') {
        // Track if this field op actually changed the record by checking updatedAt
        const oldUpdatedAt = record['updatedAt'];
        applyOperation(record, { ...op, timestamp: effectiveTs });
        if (record['updatedAt'] !== oldUpdatedAt) {
          anyFieldOpMutated = true;
        }
      } else {
        applyOperation(record, { ...op, timestamp: effectiveTs });
      }
      stats.appliedOps++;
    }

    // Determine if resurrection occurred:
    // 1. Same-delta resurrection: hasResurrection (field ops won over delete in same batch)
    // 2. Cross-delta resurrection: existing record was tombstoned AND field ops actually mutated it
    // Note: We check anyFieldOpMutated instead of just presence of field ops to avoid
    // clearing tombstones when all ops are no-ops (e.g., blocked dangerous path segments)
    const needsResurrection = hasResurrection || (wasTombstoned && anyFieldOpMutated);

    // Handle resurrection: clear tombstone fields if resurrecting
    if (needsResurrection) {
      delete record['deletedAt'];
      delete record['deletedByDevice'];
      stats.resurrections++;
    }

    modifiedRecords.set(recordKey, record);
    recordTables.set(recordKey, table);
  }

  return {
    modifiedRecords,
    recordTables,
    updatedLastAppliedSeq: maxSeqByDevice,
    stats,
  };
}

// ============================================================================
// Phase 3: Gap Detection
// ============================================================================

/**
 * Sequence range metadata from DeltaExport.
 * Reports min/max seq of operations included in the delta, per device.
 * Empty object {} when no operations in delta.
 * Optional only for v1 artifact compatibility.
 */
export type OpSeqRanges =
  | { [deviceId: string]: { minSeq: number; maxSeq: number } }
  | undefined;

/**
 * Compacted sequence metadata from DeltaExport.
 * Sequences that were dropped during compaction (coalesced away), per device.
 *
 * Format options per device:
 * - Individual sequences: `number[]` (e.g., `[5, 6, 7]`)
 * - Range-compressed (inclusive): `[number, number][]` (e.g., `[[5, 10], [15, 20]]`)
 *
 * Used by gap detection to distinguish compaction gaps from true missing operations.
 * Optional: absent when no compaction occurred.
 */
export type CompactedSeqs =
  | { [deviceId: string]: number[] | [number, number][] }
  | undefined;

/**
 * Represents a forward gap in the operation sequence.
 * A gap means we're missing operations between lastApplied and the delta's minSeq.
 */
export interface SequenceGap {
  /** Device that has the gap */
  deviceId: string;
  /** Expected next sequence number (lastApplied + 1) */
  expectedSeq: number;
  /** Actual minimum sequence in the incoming delta */
  actualMinSeq: number;
  /** Number of missing operations (actualMinSeq - expectedSeq) */
  gapSize: number;
}

/**
 * Represents a stale/out-of-order delta detection.
 * Occurs when lastApplied > maxSeq, meaning we've already applied
 * operations beyond what this delta contains.
 */
export interface StaleRange {
  /** Device with stale range */
  deviceId: string;
  /** Our last applied sequence for this device */
  lastApplied: number;
  /** Maximum sequence in the incoming delta */
  deltaMaxSeq: number;
}

/**
 * Expand compacted sequences from either format into a Set for O(1) lookup.
 *
 * Handles two formats:
 * - Individual sequences: `[5, 6, 7]` → Set{5, 6, 7}
 * - Range-compressed (inclusive): `[[5, 10], [15, 20]]` → Set{5, 6, 7, 8, 9, 10, 15, 16, 17, 18, 19, 20}
 *
 * @param seqs - Compacted sequences in either format, or undefined
 * @returns Set of all compacted sequence numbers
 *
 * @example
 * expandCompactedSeqs([5, 6, 7]); // Set{5, 6, 7}
 * expandCompactedSeqs([[5, 10]]); // Set{5, 6, 7, 8, 9, 10}
 * expandCompactedSeqs(undefined); // Set{}
 */
export function expandCompactedSeqs(
  seqs: number[] | [number, number][] | undefined
): Set<number> {
  // Handle undefined or empty array
  if (!seqs || seqs.length === 0) {
    return new Set();
  }

  const result = new Set<number>();

  // Detect format by checking first element
  const firstElement = seqs[0];
  if (Array.isArray(firstElement)) {
    // Range-compressed format: [[min, max], ...]
    for (const range of seqs as [number, number][]) {
      const [min, max] = range;
      for (let i = min; i <= max; i++) {
        result.add(i);
      }
    }
  } else {
    // Individual sequences format: [seq1, seq2, ...]
    for (const seq of seqs as number[]) {
      result.add(seq);
    }
  }

  // Guard: warn if unexpectedly large (shouldn't happen in normal usage)
  if (result.size > 10000) {
    console.warn(
      `[OperationApplicator] Unexpectedly large compactedSeqs expansion: ${result.size} sequences`
    );
  }

  return result;
}

/**
 * Result of gap detection analysis.
 */
export interface GapDetectionResult {
  /**
   * True if forward gaps were detected (missing operations).
   * Note: staleRanges are informational only and not counted as "gaps".
   */
  hasGaps: boolean;
  /** Forward gaps - missing operations we haven't seen */
  gaps: SequenceGap[];
  /** Stale ranges - delta contains ops we've already processed (informational) */
  staleRanges: StaleRange[];
}

/**
 * Detect sequence gaps by comparing incoming opSeqRanges against local tracking.
 *
 * This is a pure function - caller handles logging and any actions based on results.
 *
 * Gap detection logic:
 * - Forward gap: minSeq > lastApplied + 1 (we're missing operations)
 * - Stale range: lastApplied > maxSeq (delta is older than our state)
 * - Compaction awareness: if ALL sequences in a gap are in compactedSeqs, it's not a true gap
 *
 * Note: Current export only includes the exporting device's ops in opSeqRanges,
 * but this function iterates all deviceIds to handle future multi-device scenarios.
 *
 * @param opSeqRanges - Sequence ranges from incoming delta (optional for v1 compat)
 * @param lastAppliedSeqByDevice - Local tracking of highest applied seq per device
 * @param compactedSeqs - Sequences compacted away during export (optional for backward compat)
 * @returns Gap detection result with forward gaps and stale range warnings
 *
 * @example
 * const result = detectSequenceGaps(
 *   { 'device-A': { minSeq: 5, maxSeq: 10 } },
 *   { 'device-A': 2 }
 * );
 * // result.hasGaps === true (gap: expected 3, got 5)
 * // result.gaps === [{ deviceId: 'device-A', expectedSeq: 3, actualMinSeq: 5, gapSize: 2 }]
 *
 * @example
 * // With compactedSeqs - gap is explained by compaction
 * const result = detectSequenceGaps(
 *   { 'device-A': { minSeq: 5, maxSeq: 10 } },
 *   { 'device-A': 2 },
 *   { 'device-A': [3, 4] } // seqs 3 and 4 were compacted
 * );
 * // result.hasGaps === false (gap fully explained by compaction)
 */
export function detectSequenceGaps(
  opSeqRanges: OpSeqRanges,
  lastAppliedSeqByDevice: Record<string, number>,
  compactedSeqs?: CompactedSeqs
): GapDetectionResult {
  const gaps: SequenceGap[] = [];
  const staleRanges: StaleRange[] = [];

  // Handle undefined/empty opSeqRanges - no devices to check
  if (!opSeqRanges || Object.keys(opSeqRanges).length === 0) {
    return { hasGaps: false, gaps: [], staleRanges: [] };
  }

  // Iterate each device in the incoming opSeqRanges
  for (const deviceId of Object.keys(opSeqRanges)) {
    const range = opSeqRanges[deviceId];
    // Safety check - shouldn't happen with Object.keys, but satisfies TypeScript
    if (!range) continue;
    const { minSeq, maxSeq } = range;

    // Get our last applied seq for this device (0 if unseen)
    const lastApplied = lastAppliedSeqByDevice[deviceId] ?? 0;

    // Check for forward gap: minSeq > lastApplied + 1
    // This means we're missing operations between lastApplied and minSeq
    if (minSeq > lastApplied + 1) {
      // Check if gap is fully explained by compaction
      const deviceCompactedSeqs = compactedSeqs?.[deviceId];
      const compactedSet = expandCompactedSeqs(deviceCompactedSeqs);

      // Check if ALL sequences in the gap are compacted
      let allCompacted = compactedSet.size > 0;
      if (allCompacted) {
        for (let seq = lastApplied + 1; seq < minSeq; seq++) {
          if (!compactedSet.has(seq)) {
            allCompacted = false;
            break;
          }
        }
      }

      // Only report gap if NOT fully explained by compaction
      if (!allCompacted) {
        gaps.push({
          deviceId,
          expectedSeq: lastApplied + 1,
          actualMinSeq: minSeq,
          gapSize: minSeq - lastApplied - 1,
        });
      }
    }

    // Check for stale range: lastApplied > maxSeq
    // This means the delta contains only ops we've already processed
    if (lastApplied > maxSeq) {
      staleRanges.push({
        deviceId,
        lastApplied,
        deltaMaxSeq: maxSeq,
      });
    }
  }

  return {
    hasGaps: gaps.length > 0,
    gaps,
    staleRanges,
  };
}
